import {
  TEMPLATE_DECISION_CLASSES,
  TEMPLATE_SOURCE_NONE,
  type TemplateDecisionClass,
  type TemplateDecisionRowDto,
  type TemplateListItemDto,
  type TemplateSectionDto,
} from '@pams/shared';
import type { BookletChipVariant } from '../components/bookletChip';
import { OPEN_DECISIONS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §3a] WHAT HOLDS TEMPLATES BACK, ONE DECISION A ROW, BY HOW MANY IT FREES — pure.
 *
 *  Gathered from each BLOCKED template's own `waitsOn`, which is the decisions page's own inventory
 *  read per template — so a row here is a row on that page, under the same label, and its count is
 *  the templates that wait on it. Sorted by the templates deciding it alone would make ready
 *  (`onlyFor`), then by the templates it holds back.
 *
 *  ⚠️ `onlyFor` IS ZERO FOR EVERY ROW ON THE 2025 SET, AND THE PANEL SAYS SO. Every buildable template
 *  also waits on its section's days and times and on a policy for its columns, so no one decision
 *  makes any template ready; the order is then by templates held back. A count that hid that would
 *  promise a click that changes nothing on the screen.
 *
 *  ⚠️ A MARKER IS A SENTENCE, NEVER A ROW. "A policy, once its columns are decided" is not something
 *  a person can decide yet; the panel states how many templates wait on it, beneath the rows.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export type OpenDecisionRow = {
  readonly key: string;
  readonly class: TemplateDecisionClass;
  readonly code: string;
  readonly label: string;
  /** The blocked templates waiting on this decision, in booklet order. */
  readonly heldBack: TemplateListItemDto[];
  /** Of those, the templates this is the ONLY open decision for. */
  readonly onlyFor: number;
};

export type WaitingMarker = { readonly class: 'POLICY' | 'IN_CHARGE_ROLE'; readonly templates: number };

export type OpenDecisions = {
  /** Every open decision but the unusual columns', ranked. */
  readonly rows: OpenDecisionRow[];
  /** The open decisions on an unusual column — the unusual block's, not the main list's ([P7B.1 §3c]). */
  readonly unusual: OpenDecisionRow[];
  readonly waiting: WaitingMarker[];
  /** Distinct decisions holding at least one template back — rows and unusual together. */
  readonly holdingBack: number;
};

const classOrder = (decisionClass: TemplateDecisionClass): number => TEMPLATE_DECISION_CLASSES.indexOf(decisionClass);

export const openDecisionsOf = (
  items: readonly TemplateListItemDto[],
  rareColumns: ReadonlyMap<string, number>,
): OpenDecisions => {
  const blocked = [...items].filter((item) => item.readiness.state === 'BLOCKED').sort((a, b) => a.ordinal - b.ordinal);
  const rows = new Map<string, { row: Omit<OpenDecisionRow, 'onlyFor'>; onlyFor: number }>();
  const waiting = new Map<'POLICY' | 'IN_CHARGE_ROLE', number>();
  for (const item of blocked) {
    for (const wait of item.waitsOn) {
      if (wait.code === null) {
        if (wait.class === 'POLICY' || wait.class === 'IN_CHARGE_ROLE')
          waiting.set(wait.class, (waiting.get(wait.class) ?? 0) + 1);
        continue;
      }
      const key = `${wait.class}|${wait.code}`;
      const entry = rows.get(key) ?? {
        row: { key, class: wait.class, code: wait.code, label: wait.label, heldBack: [] },
        onlyFor: 0,
      };
      entry.row.heldBack.push(item);
      if (item.waitsOn.length === 1) entry.onlyFor += 1;
      rows.set(key, entry);
    }
  }
  const ranked = [...rows.values()]
    .map(({ row, onlyFor }): OpenDecisionRow => ({ ...row, onlyFor }))
    .sort(
      (a, b) =>
        b.onlyFor - a.onlyFor ||
        b.heldBack.length - a.heldBack.length ||
        classOrder(a.class) - classOrder(b.class) ||
        a.label.localeCompare(b.label),
    );
  const isUnusual = (row: OpenDecisionRow): boolean => row.class === 'FORCE_COLUMN' && rareColumns.has(row.code);
  return {
    rows: ranked.filter((row) => !isUnusual(row)),
    unusual: ranked.filter(isUnusual),
    waiting: (['POLICY', 'IN_CHARGE_ROLE'] as const).flatMap((decisionClass) => {
      const templates = waiting.get(decisionClass) ?? 0;
      return templates === 0 ? [] : [{ class: decisionClass, templates }];
    }),
    holdingBack: ranked.length,
  };
};

/**
 * ⚠️ [P7C §3] "NO SINGLE DECISION MAKES A TEMPLATE READY" — TRUE ONLY WHEN NO ROW FREES ONE ON ITS OWN.
 * The panel and the setup guide both say it, and both ask this, so neither can say it where the other
 * would not: on a set where one decision does free a template (`onlyFor > 0`), the sentence is false.
 */
export const noDecisionFreesATemplate = (open: Pick<OpenDecisions, 'rows'>): boolean =>
  open.rows.length > 0 && open.rows.every((row) => row.onlyFor === 0);

/**
 * ⚠️ [P7B.1 §3a] THE CODES THAT HOLD NOTHING BACK, LISTED ANYWAY. A unit named only as an in-charge's
 * guidance — "1st Bn. AP" — keeps the booklet's words when undecided, so no template waits on it; the
 * set's count of open decisions counts it all the same. Listed, with how many templates name it, so
 * that count and this panel's add up rather than leaving a person to wonder where twelve went.
 */
export const inChargeOnlyOf = (sources: readonly TemplateDecisionRowDto[] | undefined): TemplateDecisionRowDto[] =>
  (sources ?? [])
    .filter((row) => row.decision === null && row.context.class === 'SOURCE' && row.context.usedFor === 'IN_CHARGE')
    .sort((a, b) => b.usedBy - a.usedBy || a.label.localeCompare(b.label));

export type Chip = { readonly variant: BookletChipVariant; readonly text: string };

/**
 * ⚠️ [P7B.1] THE CHIP A DECISION ROW WEARS — the review site's reading of the code, where it had one.
 * A source's chip is the extraction's own status (`bundleStatus`): unmapped or unknown reads "Not in
 * PAMS", probable reads "Probable", and one the review mapped that PAMS still does not match reads
 * "Mapped in the review" — plain, because the review's mapping is a fact and not a match here.
 */
export const chipOf = (
  row: Pick<OpenDecisionRow, 'class' | 'code'>,
  sourceStatus: ReadonlyMap<string, string | null>,
  sections: readonly TemplateSectionDto[],
): Chip => {
  switch (row.class) {
    case 'SOURCE': {
      const status = sourceStatus.get(row.code) ?? null;
      if (status === 'probable') return { variant: 'accent', text: OPEN_DECISIONS_COPY.chip.probable };
      if (status === 'mapped') return { variant: 'plain', text: OPEN_DECISIONS_COPY.chip.mappedInReview };
      return { variant: 'unmapped', text: OPEN_DECISIONS_COPY.chip.notInPams };
    }
    case 'FORCE_COLUMN':
      return { variant: 'unmapped', text: OPEN_DECISIONS_COPY.chip.notMatched };
    case 'POLICY':
      return { variant: 'plain', text: OPEN_DECISIONS_COPY.chip.policy };
    case 'IN_CHARGE_ROLE':
      return { variant: 'role', text: OPEN_DECISIONS_COPY.chip.role };
    case 'TOTALS':
      return { variant: 'accent', text: OPEN_DECISIONS_COPY.chip.differs };
    case 'SCHEDULE':
      return sections.find((section) => section.key === row.code)?.proposal
        ? { variant: 'accent', text: OPEN_DECISIONS_COPY.chip.proposed }
        : { variant: 'plain', text: OPEN_DECISIONS_COPY.chip.schedule };
    case 'DUTY_POINT':
      return { variant: 'plain', text: OPEN_DECISIONS_COPY.chip.dutyPoint };
  }
};

/** The extraction's status of each source code, from the decisions page's own `SOURCE` list. */
export const sourceStatusOf = (sources: readonly TemplateDecisionRowDto[] | undefined): Map<string, string | null> =>
  new Map(
    (sources ?? []).map((row) => [row.code, row.context.class === 'SOURCE' ? row.context.bundleStatus : null] as const),
  );

/**
 * A row in words: the class's noun and the code. A printed total and a duty point name their
 * TEMPLATE by title — the code (`p10:t1`, `t2.g1.d1`) means nothing to a person.
 * [amended TEMPLATE-FIND] A duty point also names ITSELF, by the server label's part after ` · ` —
 * `(b)` or `table 1, row 34` (`templateDutyPointLabelOf`, which never itself contains ` · `).
 */
export const rowWordsOf = (row: OpenDecisionRow): string => {
  const [only] = row.heldBack;
  switch (row.class) {
    case 'SOURCE':
      return row.code === TEMPLATE_SOURCE_NONE
        ? OPEN_DECISIONS_COPY.words.blankSource
        : OPEN_DECISIONS_COPY.words.source(row.label);
    case 'FORCE_COLUMN':
      return OPEN_DECISIONS_COPY.words.column(row.label);
    case 'POLICY':
      return OPEN_DECISIONS_COPY.words.policy(row.label);
    case 'IN_CHARGE_ROLE':
      return OPEN_DECISIONS_COPY.words.role(row.label);
    case 'TOTALS':
      return OPEN_DECISIONS_COPY.words.totals(only?.title ?? row.label);
    case 'SCHEDULE':
      return OPEN_DECISIONS_COPY.words.schedule(row.label);
    case 'DUTY_POINT':
      return OPEN_DECISIONS_COPY.words.dutyPoint(only?.title ?? row.label, row.label.split(' · ')[1] ?? null);
  }
};
